import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { maskDocument } from "@/lib/format";
import { WhatsAppCTA } from "@/components/WhatsApp";

const SVC_LABELS = { plasma: "Corte Plasma", laser: "Corte Laser", dobra: "Dobra", jateamento: "Jateamento", gravacao: "Gravação a Laser" };
import { Loader2, Search, Hash, AtSign, ChevronRight, ArrowLeft, CheckCircle2, XCircle } from "lucide-react";
import { StatusBadge, brl, fdate } from "@/pages/portal/Dashboard";

const fdt = (iso) =>
  iso ? new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : "—";

export default function AcompanharOrcamento() {
  const [params] = useSearchParams();
  const [doc, setDoc] = useState(maskDocument(params.get("d") || ""));
  const [email, setEmail] = useState(params.get("c") || "");
  const [session, setSession] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [deciding, setDeciding] = useState(false);

  const API_BASE = process.env.REACT_APP_BACKEND_URL;

  const decide = async (decision) => {
    const label = decision === "aprovar" ? "APROVAR" : "RECUSAR";
    if (!window.confirm(`Confirmar: ${label} o orçamento ${detail.number}?`)) return;
    setDeciding(true);
    setError("");
    try {
      const { data } = await api.post(`/quote-requests/track/${encodeURIComponent(detail.number)}/decision`, { contact: email, decision });
      if (data?.op_error) {
        toast.warning(data.op_error);
      } else {
        toast.success(decision === "aprovar" ? "Orçamento aprovado! A Alpha Metais foi notificada." : "Orçamento recusado. A Alpha Metais foi notificada.");
      }
      openQuote(detail.number);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setDeciding(false);
    }
  };
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSession(null);
    setDetail(null);
    try {
      const { data } = await api.post("/track/login", { document: doc, email });
      setSession(data);
      const n = params.get("n");
      if (n && data.quotes.some((q) => q.number === n)) openQuote(n, email);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const openQuote = async (number, mail = email) => {
    setDetailLoading(true);
    try {
      const { data } = await api.get(`/quote-requests/track/${encodeURIComponent(number)}`, {
        params: { contact: mail },
      });
      setDetail(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setDetailLoading(false);
    }
  };

  const inputCls =
    "mt-1.5 w-full bg-zinc-900/70 border border-zinc-700 rounded-sm px-3 py-2.5 text-sm outline-none focus:border-orange-500 transition-colors placeholder:text-zinc-600";
  const labelCls = "text-xs uppercase tracking-wider text-zinc-400";

  return (
    <div className="pt-[72px]" data-testid="track-page">
      <section className="py-20 border-b border-zinc-800 bg-zinc-900/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-3 mb-3">
            <span className="h-px w-10 bg-orange-500" />
            <span className="mono text-xs uppercase tracking-[0.35em] text-orange-400">acompanhamento</span>
          </div>
          <h1 className="display text-4xl sm:text-6xl font-extrabold uppercase">Acompanhe seu Orçamento</h1>
          <p className="mt-4 text-zinc-400 max-w-2xl text-sm sm:text-base">
            Sem login: informe o CPF/CNPJ e o e-mail usados na solicitação para ver todo o seu histórico.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          {!session && (
            <form onSubmit={submit} className="card-tactical rounded-md p-7" data-testid="track-form">
              <div>
                <label className={labelCls}>CPF / CNPJ *</label>
                <div className="relative">
                  <Hash className="w-4 h-4 text-zinc-600 absolute left-3 top-1/2 -translate-y-1/2 mt-0.5" />
                  <input data-testid="track-number-input" required value={doc} onChange={(e) => setDoc(maskDocument(e.target.value))}
                    className={`${inputCls} pl-9 mono`} placeholder="000.000.000-00 ou 00.000.000/0000-00" />
                </div>
              </div>
              <div className="mt-5">
                <label className={labelCls}>E-mail da solicitação *</label>
                <div className="relative">
                  <AtSign className="w-4 h-4 text-zinc-600 absolute left-3 top-1/2 -translate-y-1/2 mt-0.5" />
                  <input data-testid="track-contact-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                    className={`${inputCls} pl-9`} placeholder="voce@empresa.com.br" />
                </div>
              </div>
              {error && <div className="mt-4 text-sm text-red-400" data-testid="track-error">{error}</div>}
              <button data-testid="track-submit-button" type="submit" disabled={loading}
                className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-500 disabled:opacity-60 text-white font-semibold uppercase tracking-wider px-6 py-3.5 rounded-sm transition-colors">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                Ver meus orçamentos
              </button>
            </form>
          )}

          {session && !detail && (
            <div data-testid="track-result">
              <div className="flex items-center justify-between gap-3">
                <h2 className="display text-2xl font-bold uppercase">
                  Olá, <span className="text-orange-500">{session.name}</span>
                </h2>
                <button onClick={() => setSession(null)} data-testid="track-new-search"
                  className="text-xs uppercase tracking-wider text-zinc-500 hover:text-orange-400">
                  Nova consulta
                </button>
              </div>
              <p className="mt-1 text-sm text-zinc-500">Seus orçamentos ({session.quotes.length}):</p>
              <ul className="mt-5 space-y-3">
                {session.quotes.map((q) => (
                  <li key={q.number}>
                    <button
                      data-testid={`track-quote-${q.number}`}
                      onClick={() => openQuote(q.number)}
                      className="w-full text-left card-tactical rounded-md p-5 hover:border-orange-900/60 transition-colors group"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <span className="mono text-orange-500 font-bold">{q.number}</span>
                        <StatusBadge status={q.status} />
                      </div>
                      <div className="mt-2 text-sm text-zinc-300">{(q.services || []).join(" + ") || "—"}</div>
                      <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-zinc-500">
                        <span>Peça: {q.part_name || "—"}</span>
                        <span>Qtd: {q.quantity ?? "—"}</span>
                        <span>{fdate(q.created_at)}</span>
                        <span className="mono">{q.value != null ? brl(q.value) : ""}</span>
                      </div>
                      <span className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-widest text-zinc-600 group-hover:text-orange-500 transition-colors">
                        Ver detalhes <ChevronRight className="w-3.5 h-3.5" />
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {detailLoading && (
            <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-zinc-600" /></div>
          )}

          {session && detail && !detailLoading && (
            <div data-testid="track-detail">
              <button onClick={() => setDetail(null)} data-testid="track-back-to-list"
                className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-zinc-500 hover:text-orange-400 transition-colors">
                <ArrowLeft className="w-3.5 h-3.5" /> Voltar à lista
              </button>
              <div className="mt-4 card-tactical rounded-md p-7">
                <div className="flex items-center justify-between gap-3">
                  <div className="mono text-xl font-bold text-orange-500" data-testid="track-result-number">{detail.number}</div>
                  <StatusBadge status={detail.status} />
                </div>
                <div className="mt-5 space-y-3 text-sm">
                  <div className="flex justify-between gap-4"><span className="text-zinc-500">Data da solicitação</span><span className="text-zinc-200">{fdate(detail.created_at)}</span></div>
                  <div className="flex justify-between gap-4"><span className="text-zinc-500">Serviço(s)</span><span className="text-zinc-200 text-right">{(detail.services || []).join(" + ") || "—"}</span></div>
                  <div className="flex justify-between gap-4"><span className="text-zinc-500">Peça</span><span className="text-zinc-200 text-right">{detail.part_name || "—"}</span></div>
                  <div className="flex justify-between gap-4"><span className="text-zinc-500">Quantidade</span><span className="text-zinc-200">{detail.quantity ?? "—"}</span></div>
                  <div className="flex justify-between gap-4 border-t border-zinc-800 pt-3">
                    <span className="text-zinc-500">Valor</span>
                    <span className="mono text-orange-400" data-testid="track-result-value">{detail.value != null ? brl(detail.value) : "Aguardando análise"}</span>
                  </div>
                  {detail.value != null && (
                    <div className="flex justify-between gap-4">
                      <span className="text-zinc-500">Documento</span>
                      <a
                        href={`${API_BASE}/api/quote-requests/track/${encodeURIComponent(detail.number)}/pdf?contact=${encodeURIComponent(email)}`}
                        target="_blank" rel="noreferrer" data-testid="track-pdf-link"
                        className="text-orange-500 hover:text-orange-400 font-medium"
                      >
                        Visualizar PDF
                      </a>
                    </div>
                  )}
                  <div className="flex justify-between gap-4 items-center border-t border-zinc-800 pt-3">
                    <span className="text-zinc-500">Dúvidas sobre o orçamento</span>
                    <WhatsAppCTA
                      message={`Olá, gostaria de falar sobre o orçamento ${detail.number}.`}
                      label="Falar no WhatsApp"
                      testId="track-whatsapp-cta"
                      className="!px-4 !py-2 !text-xs"
                    />
                  </div>
                  {!!detail.items?.length && (
                    <div className="border-t border-zinc-800 pt-3" data-testid="track-items">
                      <span className="text-zinc-500 block mb-2">Itens do orçamento</span>
                      <ul className="space-y-3">
                        {detail.items.map((it) => (
                          <li key={it.seq} className="flex gap-3 items-start" data-testid={`track-item-${it.seq}`}>
                            {it.has_thumbnail && (
                              <img
                                src={`${API_BASE}/api/quote-requests/track/${encodeURIComponent(detail.number)}/items/${it.seq}/thumbnail?contact=${encodeURIComponent(email)}`}
                                alt={`Peça ${it.part_name}`}
                                data-testid={`track-item-thumb-${it.seq}`}
                                className="w-[140px] h-[110px] object-contain object-center bg-white rounded-sm p-1.5 shrink-0"
                              />
                            )}
                            <div className="min-w-0 text-xs">
                              <div className="text-zinc-200 font-semibold">ITEM {String(it.seq).padStart(3, "0")} — {it.part_name}</div>
                              <div className="text-zinc-500 mt-0.5">
                                {it.item_type === "jateamento" && it.jateamento ? (
                                  <>
                                    Jateamento
                                    {" · "}{it.jateamento.length_mm}×{it.jateamento.width_mm}×{it.jateamento.height_mm} mm
                                    {" · "}{it.jateamento.faces} faces
                                    {it.jateamento.difficulty ? ` · Dificuldade ${it.jateamento.difficulty}` : ""}
                                    {it.jateamento.area_estimate_m2 != null ? ` · Área estimada ${it.jateamento.area_estimate_m2} m²` : ""}
                                    {" · "}{it.quantity} un.
                                    {it.jateamento.photos_count ? ` · ${it.jateamento.photos_count} foto(s)` : ""}
                                  </>
                                ) : (
                                  <>
                                    {(it.services || []).map((s) => SVC_LABELS[s] || s).join(" + ") || "—"}
                                    {" · "}{it.material}{it.thickness_mm ? ` ${it.thickness_mm} mm` : ""}{it.grade ? ` ${it.grade}` : ""}
                                    {" · "}{it.quantity} un.
                                  </>
                                )}
                              </div>
                              <div className="mono text-orange-400 mt-0.5">
                                {it.unit_price != null ? `${brl(it.unit_price)}/un` : ""}{it.total != null ? ` · Total ${brl(it.total)}` : ""}
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {detail.client_notes && (
                    <div className="border-t border-zinc-800 pt-3" data-testid="track-result-notes">
                      <span className="text-zinc-500 block mb-1">Observações</span>
                      <span className="text-zinc-300">{detail.client_notes}</span>
                    </div>
                  )}
                </div>
                {["Orçamento enviado", "Aguardando aprovação"].includes(detail.status) && (
                  <div className="mt-6 border-t border-zinc-800 pt-5" data-testid="track-decision">
                    <div className="text-xs uppercase tracking-widest text-zinc-500 mb-3">Responder orçamento</div>
                    <div className="flex flex-wrap gap-3">
                      <button
                        data-testid="track-approve-button"
                        onClick={() => decide("aprovar")}
                        disabled={deciding}
                        className="flex-1 min-w-[150px] inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-60 text-white text-sm font-semibold uppercase tracking-wider px-5 py-3 rounded-sm transition-colors"
                      >
                        {deciding ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                        Aprovar orçamento
                      </button>
                      <button
                        data-testid="track-reject-button"
                        onClick={() => decide("recusar")}
                        disabled={deciding}
                        className="inline-flex items-center justify-center gap-2 border border-zinc-700 hover:border-red-500 text-zinc-300 hover:text-red-400 text-sm font-semibold uppercase tracking-wider px-5 py-3 rounded-sm transition-colors"
                      >
                        <XCircle className="w-4 h-4" /> Recusar
                      </button>
                    </div>
                  </div>
                )}
                {!!detail.timeline?.length && (
                  <div className="mt-6 border-t border-zinc-800 pt-5" data-testid="track-timeline">
                    <div className="text-xs uppercase tracking-widest text-zinc-500 mb-3">Linha do tempo</div>
                    <ul className="space-y-3">
                      {detail.timeline.map((t, i) => (
                        <li key={i} className="flex gap-3 text-sm">
                          <span className="mono text-xs text-zinc-600 whitespace-nowrap pt-0.5">{fdt(t.at)}</span>
                          <span className="text-zinc-300">
                            {t.event}{t.to ? <> — <span className="text-orange-400">{t.to}</span></> : ""}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
